import * as z from "zod/v4";
import { EmailBlockType } from "../email-builder";

export enum ClickableElementInteractionType {
  /** Anything that is not an <a> element, e.g., shoppable product carousel forward/backward buttons */
  INTERACTIVE = "interactive",
  /** <a> elements */
  LINK = "link",
}

export enum ClickableElementIdentifiability {
  /**
   * Used by elements that have a purpose known to Redo in advance.
   * For example, a shoppable product block's checkout button.
   * This gives us the ability to semantically label the element in the analytics UI.
   */
  NAMED = "named",
  /**
   * Used by elements that do not have a purpose known to Redo in advance.
   * For example, an arbitrary link in a text block.
   */
  ANONYMOUS = "anonymous",
}

export interface NamedClickableElement {
  idSuffix: string;
  interactionType: ClickableElementInteractionType;
  identifiability: ClickableElementIdentifiability.NAMED;
}

export interface AnonymousClickableElement {
  idSuffix: string;
  interactionType: ClickableElementInteractionType;
  identifiability: ClickableElementIdentifiability.ANONYMOUS;
}

export type ClickableElement =
  | NamedClickableElement
  | AnonymousClickableElement;

export type NamedElementId =
  `${ClickableElementIdentifiability.NAMED}_${string}`;

export type AnonymousElementId =
  `${ClickableElementIdentifiability.ANONYMOUS}_${string}`;

export type ElementId = NamedElementId | AnonymousElementId;

export function namedElementId(element: NamedClickableElement): NamedElementId {
  return `${ClickableElementIdentifiability.NAMED}_${element.idSuffix}`;
}

export function anonymousElementId(
  element: AnonymousClickableElement,
): AnonymousElementId {
  return `${ClickableElementIdentifiability.ANONYMOUS}_${element.idSuffix}`;
}

export function elementId(element: ClickableElement): ElementId {
  if (element.identifiability === ClickableElementIdentifiability.NAMED) {
    return namedElementId(element);
  } else {
    return anonymousElementId(element);
  }
}

export function parseElementId(
  input: string | undefined,
): ElementId | undefined {
  const split = input?.split("_");
  if (!split || split.length < 2) {
    return undefined;
  }
  const prefix = split[0];
  if (prefix === ClickableElementIdentifiability.NAMED) {
    return input as NamedElementId;
  }
  if (prefix === ClickableElementIdentifiability.ANONYMOUS) {
    return input as AnonymousElementId;
  }
  return undefined;
}

export const namedClickableElements = {
  [EmailBlockType.SHOPPABLE_PRODUCTS]: {
    ADD_TO_CART_BTN: {
      idSuffix: "add-to-cart-btn",
      interactionType: ClickableElementInteractionType.INTERACTIVE,
      identifiability: ClickableElementIdentifiability.NAMED,
    },
    CAROUSEL_FORWARD_BTN: {
      idSuffix: "carousel-forward-btn",
      interactionType: ClickableElementInteractionType.INTERACTIVE,
      identifiability: ClickableElementIdentifiability.NAMED,
    },
    CAROUSEL_BACKWARD_BTN: {
      idSuffix: "carousel-backward-btn",
      interactionType: ClickableElementInteractionType.INTERACTIVE,
      identifiability: ClickableElementIdentifiability.NAMED,
    },
    VARIANT_DROPDOWN: {
      idSuffix: "variant-dropdown",
      interactionType: ClickableElementInteractionType.INTERACTIVE,
      identifiability: ClickableElementIdentifiability.NAMED,
    },
    CHECKOUT_BTN: {
      idSuffix: "checkout-btn",
      interactionType: ClickableElementInteractionType.INTERACTIVE,
      identifiability: ClickableElementIdentifiability.NAMED,
    },
  },
  [EmailBlockType.BUTTON]: {}, // only has anonymous links
  [EmailBlockType.HEADER]: {
    HEADER_LINK: {
      idSuffix: "header-link",
      interactionType: ClickableElementInteractionType.LINK,
      identifiability: ClickableElementIdentifiability.NAMED,
    },
  },
  [EmailBlockType.FOOTER]: {
    UNSUBSCRIBE_LINK: {
      idSuffix: "unsubscribe-link",
      interactionType: ClickableElementInteractionType.LINK,
      identifiability: ClickableElementIdentifiability.NAMED,
    },
  },
  [EmailBlockType.IMAGE]: {
    CLICKTHROUGH_LINK: {
      idSuffix: "clickthrough-link",
      interactionType: ClickableElementInteractionType.LINK,
      identifiability: ClickableElementIdentifiability.NAMED,
    },
  },
  [EmailBlockType.TEXT]: {}, // only has anonymous links
  [EmailBlockType.SPACER]: {},
  [EmailBlockType.LINE]: {},
  [EmailBlockType.REVIEW_REQUEST]: {
    REVIEW_REQUEST_LINK: {
      idSuffix: "review-request-link",
      interactionType: ClickableElementInteractionType.LINK,
      identifiability: ClickableElementIdentifiability.NAMED,
    },
  },
  [EmailBlockType.COLUMN]: {},
  [EmailBlockType.MENU]: {}, // only has anonymous links
  [EmailBlockType.SOCIALS]: {},
  [EmailBlockType.DISCOUNT]: {},
  [EmailBlockType.SCRATCH_TO_REVEAL]: {
    SCRATCH_OFF_SECTION: {
      idSuffix: "scratch-off-section",
      interactionType: ClickableElementInteractionType.LINK,
      identifiability: ClickableElementIdentifiability.NAMED,
    },
  },
  [EmailBlockType.INTERACTIVE_REVIEW_REQUEST]: {
    REVIEW_REQUEST_LINK: {
      idSuffix: "review-request-link",
      interactionType: ClickableElementInteractionType.LINK, // on the fallback
      identifiability: ClickableElementIdentifiability.NAMED,
    },
    STAR_INPUT_1: {
      idSuffix: "star-input-1",
      interactionType: ClickableElementInteractionType.INTERACTIVE,
      identifiability: ClickableElementIdentifiability.NAMED,
    },
    STAR_INPUT_2: {
      idSuffix: "star-input-2",
      interactionType: ClickableElementInteractionType.INTERACTIVE,
      identifiability: ClickableElementIdentifiability.NAMED,
    },
    STAR_INPUT_3: {
      idSuffix: "star-input-3",
      interactionType: ClickableElementInteractionType.INTERACTIVE,
      identifiability: ClickableElementIdentifiability.NAMED,
    },
    STAR_INPUT_4: {
      idSuffix: "star-input-4",
      interactionType: ClickableElementInteractionType.INTERACTIVE,
      identifiability: ClickableElementIdentifiability.NAMED,
    },
    STAR_INPUT_5: {
      idSuffix: "star-input-5",
      interactionType: ClickableElementInteractionType.INTERACTIVE,
      identifiability: ClickableElementIdentifiability.NAMED,
    },
    REVIEW_TITLE: {
      idSuffix: "review-title",
      interactionType: ClickableElementInteractionType.INTERACTIVE,
      identifiability: ClickableElementIdentifiability.NAMED,
    },
    REVIEW_DESCRIPTION: {
      idSuffix: "review-description",
      interactionType: ClickableElementInteractionType.INTERACTIVE,
      identifiability: ClickableElementIdentifiability.NAMED,
    },
    SUBMIT_REVIEW_LINK: {
      idSuffix: "submit-review-link",
      interactionType: ClickableElementInteractionType.INTERACTIVE, // TODO: decide whether to count this as LINK (see Figma)
      identifiability: ClickableElementIdentifiability.NAMED,
    },
  },
  [EmailBlockType.TRACKING_INFO]: {
    TRACKING_LINK: {
      idSuffix: "tracking-link",
      interactionType: ClickableElementInteractionType.LINK,
      identifiability: ClickableElementIdentifiability.NAMED,
    },
  },
  [EmailBlockType.PRODUCTS]: {
    PRODUCT_IMAGE_LINK: {
      idSuffix: "product-image-link",
      interactionType: ClickableElementInteractionType.LINK,
      identifiability: ClickableElementIdentifiability.NAMED,
    },
    ADD_TO_CART_LINK: {
      idSuffix: "add-to-cart-link",
      interactionType: ClickableElementInteractionType.LINK,
      identifiability: ClickableElementIdentifiability.NAMED,
    },
    CHECKOUT_LINK: {
      idSuffix: "checkout-link",
      interactionType: ClickableElementInteractionType.LINK,
      identifiability: ClickableElementIdentifiability.NAMED,
    },
  },
  [EmailBlockType.TRACKABLE_SUMMARY]: {},
  [EmailBlockType.SUBSCRIPTION_SKIP_OR_DELAY]: {
    SKIP_ACTION_BTN: {
      idSuffix: "skip-btn",
      interactionType: ClickableElementInteractionType.INTERACTIVE,
      identifiability: ClickableElementIdentifiability.NAMED,
    },
    MANAGE_SUBSCRIPTION_SKIP_SUCCESS_LINK: {
      idSuffix: "manage-subscription-skip-success-link",
      interactionType: ClickableElementInteractionType.LINK,
      identifiability: ClickableElementIdentifiability.NAMED,
    },
    MANAGE_SUBSCRIPTION_DELAY_SUCCESS_LINK: {
      idSuffix: "manage-subscription-delay-success-link",
      interactionType: ClickableElementInteractionType.LINK,
      identifiability: ClickableElementIdentifiability.NAMED,
    },
    MANAGE_SUBSCRIPTION_CHARGE_PROCESSED_LINK: {
      idSuffix: "manage-subscription-charge-processed-link",
      interactionType: ClickableElementInteractionType.LINK,
      identifiability: ClickableElementIdentifiability.NAMED,
    },
  },
  [EmailBlockType.SUBSCRIPTION_ADD_TO_ORDER]: {},
  [EmailBlockType.SUBSCRIPTION_SWAP_PRODUCT]: {},
  [EmailBlockType.CHROME_DINO]: {},
} as const satisfies Record<
  EmailBlockType,
  Record<string, NamedClickableElement>
>;

export function idSuffixToInteractionType(
  idSuffix: string | undefined,
  blockType: EmailBlockType,
): ClickableElementInteractionType | undefined {
  if (!idSuffix) {
    return undefined;
  }

  const elements = namedClickableElements[blockType];
  for (const element of Object.values(elements)) {
    if (element.idSuffix === idSuffix) {
      return element.interactionType;
    }
  }
  return undefined;
}

export function idSuffix(elementId: ElementId | undefined): string | undefined {
  if (!elementId) {
    return undefined;
  }
  const split = elementId.split("_");
  if (split.length < 2) {
    return undefined;
  }
  return split.slice(1).join("_");
}

export const emailClickData = z.object({
  blockId: z.string(),
  elementId: z.string(),
  uniqueCustomerClicks: z.number(), // Count of unique customers who clicked
  totalClicks: z.number(), // Total number of clicks (including multiple clicks by same customer)
});
export type EmailClickData = z.infer<typeof emailClickData>;
